/**
 * « L'apparence de <prénom> » — /settings/children/:childId/appearance
 * (docs/plans/2026-09-27-chouette-planche-enfant.md, lot 2).
 *
 * Two views on one route:
 *   - "form": guided choices only (no photo, ever: D2), chips with colour
 *     swatches where it helps on a phone. Saving calls `PUT .../appearance`,
 *     then launches a batch when this description has no sheet yet
 *     (`shouldAutoStartBatch`).
 *   - "sheets": a calm waiting state while `generating` (polled every few
 *     seconds, resumed on re-entry since the first `GET .../sheets` says so),
 *     then the candidates as large cards to choose from, with "Me proposer
 *     d'autres personnages" while the allowance lasts.
 *
 * Optional by design (D8): opened with `?new=1` right after a child is
 * created, it offers "Plus tard" and the profile works without it. The copy
 * never promises resemblance (plan §5): "un personnage inspiré de Léa".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, Loader2, Moon, Sparkles } from "lucide-react";
import {
  BackButton, Button, Card, CharCounter, Container, ErrorBanner, Headline, Input, Label, MutedText, PageLayout,
  Textarea,
} from "../components/ui";
import { useChildren } from "../lib/childrenStore";
import { chooseChildSheet, getChildSheets, putChildAppearance, startChildSheets } from "../lib/dataSource";
import { useChildSheetImage } from "../lib/childSheetImage";
import { useChildSheetThumbnail } from "../lib/childSheetThumbnail";
import {
  CHILD_DETAIL_MAX_LENGTH,
  clampChildDetail,
  clampCompanionDetail,
  clampCompanionKindOther,
  COMPANION_COLOR_OPTIONS,
  COMPANION_DETAIL_MAX_LENGTH,
  COMPANION_KIND_OPTIONS,
  COMPANION_KIND_OTHER_MAX_LENGTH,
  DEFAULT_APPEARANCE_DRAFT,
  HAIR_COLOR_OPTIONS,
  HAIR_LENGTH_OPTIONS,
  HAIR_TEXTURE_OPTIONS,
  HAIRSTYLE_OPTIONS,
  isAppearanceComplete,
  PRESENTED_AS_OPTIONS,
  PYJAMA_COLOR_OPTIONS,
  PYJAMA_PATTERN_OPTIONS,
  SKIN_OPTIONS,
  type DraftAppearance,
} from "../lib/appearance";
import {
  canRequestNewBatch,
  chosenSheet,
  currentCandidates,
  otherCandidates,
  pendingSheetPlaceholders,
  sheetErrorKey,
  shouldAutoStartBatch,
  shouldPollSheets,
  staleChosenSheet,
} from "../lib/childSheets";
import type { AppearanceInput, ChildAppearance, ChildSheet, ChildSheetsOut } from "../lib/types";

const POLL_MS = 4000;

function draftFrom(appearance: ChildAppearance | null | undefined): DraftAppearance {
  if (!appearance) return { ...DEFAULT_APPEARANCE_DRAFT };
  const { companion_detail_en: _en, ...rest } = appearance;
  return { ...DEFAULT_APPEARANCE_DRAFT, ...rest };
}

interface ChoiceOption<T extends string> {
  value: T;
  labelKey: string;
  swatch?: string;
}

/** One question of the form: a radio group drawn as chips, with a colour
 *  dot when the option carries a swatch. */
function ChoiceGroup<T extends string>({
  label, options, value, onChange,
}: {
  label: string;
  options: ChoiceOption<T>[];
  value: T | undefined;
  onChange: (value: T) => void;
}) {
  const { t } = useTranslation();
  return (
    <fieldset>
      <legend className="block text-xs font-heading font-semibold uppercase tracking-wide text-violet-soft mb-1.5">
        {label}
      </legend>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={`tap-target inline-flex items-center gap-2 rounded-pill px-3 py-2 text-sm font-heading font-semibold border overprint overprint-sm ${
                active ? "bg-sun text-mint-ink border-sun ink-coral" : "bg-surface text-violet-soft border-line ink-line"
              }`}
            >
              {option.swatch && (
                <span
                  aria-hidden
                  className="w-4 h-4 rounded-full border border-night/40 shrink-0"
                  style={{ backgroundColor: option.swatch }}
                />
              )}
              {t(option.labelKey)}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

const YES_NO: ChoiceOption<"yes" | "no">[] = [
  { value: "no", labelKey: "appearance.glassesNo" },
  { value: "yes", labelKey: "appearance.glassesYes" },
];

/** A candidate (or the chosen) sheet as a large card. The image is private:
 *  fetched with the auth header through `useChildSheetImage`. */
function SheetCard({
  childId, sheet, name, chosen, busy, onChoose, chooseLabel,
}: {
  childId: string;
  sheet: ChildSheet;
  name: string;
  chosen: boolean;
  busy: boolean;
  onChoose?: () => void;
  chooseLabel?: string;
}) {
  const { t } = useTranslation();
  const { url, failed } = useChildSheetImage(childId, sheet.id);
  return (
    <Card className="flex flex-col gap-3 !p-3">
      <div className="relative w-full aspect-[3/2] rounded-card bg-night overflow-hidden">
        {url ? (
          <img
            src={url}
            alt={t("appearance.sheetAlt", { name })}
            className="absolute inset-0 w-full h-full object-contain motion-safe:animate-fade-in"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-violet-muted text-sm">
            {failed ? t("appearance.imageFailed") : <Loader2 size={20} className="animate-spin" aria-hidden />}
          </div>
        )}
      </div>
      {chosen ? (
        <p className="flex items-center justify-center gap-2 text-mint font-heading font-semibold text-sm py-2">
          <Check size={16} aria-hidden />
          {t("appearance.chosenBadge")}
        </p>
      ) : (
        onChoose && (
          <Button onClick={onChoose} disabled={busy}>
            {chooseLabel ?? t("appearance.choose")}
          </Button>
        )
      )}
    </Card>
  );
}

/**
 * The single animated "still drawing" card — shown in place of the
 * proposal, for the first request and for every "Regénérer" (plan, lot 2
 * revision: one sheet per request now, so there is only ever one of these,
 * never a row of them). No percentage: the API gives none for a request,
 * and a fake one would be a lie. A breathing moon, a couple of twinkling
 * stars, and a plain sentence instead. */
function DrawingCard({ name }: { name: string }) {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col gap-3 !p-3 motion-safe:animate-breathe">
      <div className="relative w-full aspect-[3/2] rounded-card bg-night overflow-hidden flex items-center justify-center">
        <Moon size={28} className="text-sun motion-safe:animate-twinkle" aria-hidden />
        <Sparkles
          size={14}
          className="text-lilas absolute top-5 right-9 motion-safe:animate-twinkle"
          style={{ animationDelay: "0.4s" }}
          aria-hidden
        />
        <Sparkles
          size={12}
          className="text-mint absolute bottom-6 left-9 motion-safe:animate-twinkle"
          style={{ animationDelay: "0.9s" }}
          aria-hidden
        />
      </div>
      <div className="text-center py-1" aria-live="polite">
        <p className="font-heading font-semibold text-paper text-sm">{t("appearance.waitingTitle", { name })}</p>
        <MutedText className="mt-1">{t("appearance.waitingHint")}</MutedText>
      </div>
    </Card>
  );
}

/** A small square thumbnail in the discreet "Propositions précédentes" row —
 *  tap to view it large again (plan, lot 2: older proposals of the same
 *  description stay reachable, so a parent who preferred an earlier drawing
 *  can still pick it). Prefers the backend's centred portrait, falling back
 *  to the full preview's fixed crop (see `useChildSheetThumbnail`). */
function SheetThumbnailButton({
  childId, sheet, name, onSelect, disabled,
}: {
  childId: string;
  sheet: ChildSheet;
  name: string;
  onSelect: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { url, crop } = useChildSheetThumbnail(childId, sheet.id);
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-label={t("appearance.sheetAlt", { name })}
      className="relative w-16 h-16 shrink-0 rounded-card bg-night border border-line overflow-hidden disabled:opacity-50"
    >
      {url && (
        <img
          src={url}
          alt=""
          className={crop ? "absolute max-w-none h-auto" : "absolute inset-0 w-full h-full object-cover"}
          style={crop ?? undefined}
        />
      )}
    </button>
  );
}

export default function ChildAppearance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId = "" } = useParams<{ childId: string }>();
  const [params] = useSearchParams();
  const justCreated = params.get("new") === "1";
  const { children, loading, refresh } = useChildren();
  const child = children.find((c) => c.id === childId) ?? null;

  const [view, setView] = useState<"form" | "sheets" | null>(null);
  const [draft, setDraft] = useState<DraftAppearance>(() => draftFrom(child?.appearance));
  const [sheets, setSheets] = useState<ChildSheetsOut | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const initialisedRef = useRef(false);

  // First render with the child known: pick the view, seed the draft.
  useEffect(() => {
    if (!child || initialisedRef.current) return;
    initialisedRef.current = true;
    setDraft(draftFrom(child.appearance));
    setView(child.appearance ? "sheets" : "form");
  }, [child]);

  // Unknown child (deleted, or a stale link) once the list has loaded.
  useEffect(() => {
    if (!loading && children.length > 0 && !child) navigate("/settings", { replace: true });
  }, [loading, children.length, child, navigate]);

  const loadSheets = useCallback(async () => {
    const out = await getChildSheets(childId);
    setSheets(out);
    return out;
  }, [childId]);

  useEffect(() => {
    if (!childId) return;
    void loadSheets().catch(() => setErrorKey("appearance.errors.generic"));
  }, [childId, loadSheets]);

  // Poll while a batch is running. Resumes on re-entry by construction: the
  // first GET above says `generating: true`.
  const generating = shouldPollSheets(sheets);
  useEffect(() => {
    if (!generating) return;
    let cancelled = false;
    const id = setInterval(() => {
      void getChildSheets(childId)
        .then((out) => {
          if (cancelled) return;
          setSheets(out);
          if (!out.generating) void refresh();
        })
        .catch(() => {
          // A missed tick is harmless: the next one tries again.
        });
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [generating, childId, refresh]);

  const name = child?.first_name ?? "";
  const candidates = useMemo(() => currentCandidates(sheets?.sheets ?? []), [sheets]);
  const chosen = useMemo(() => chosenSheet(sheets?.sheets ?? []), [sheets]);
  const stale = staleChosenSheet(child);
  const complete = isAppearanceComplete(draft);

  // Which current-description candidate is shown large: the parent can tap
  // an older one in the "Propositions précédentes" row to bring it back up
  // (plan, lot 2). Snaps back to the newest one whenever the candidate set
  // changes size — a fresh "Regénérer" always surfaces its new proposal by
  // default, a manual tap sticks until then.
  const [displayedId, setDisplayedId] = useState<string | null>(null);
  useEffect(() => {
    setDisplayedId(null);
  }, [candidates.length]);
  const displayed = candidates.find((c) => c.id === displayedId) ?? candidates[0] ?? null;

  // One sheet per request now (plan, lot 2 revision): a running request
  // shows a single `DrawingCard` in place of the proposal, and — while it
  // runs — every current candidate (including the one that was displayed)
  // moves into the discreet row below instead of staying in the big slot.
  const showDrawingCard = pendingSheetPlaceholders(generating) > 0;
  const previousRow = showDrawingCard ? candidates : otherCandidates(sheets?.sheets ?? [], displayed?.id ?? null);

  function set<K extends keyof DraftAppearance>(key: K, value: DraftAppearance[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function startBatch() {
    setBusy(true);
    setErrorKey(null);
    try {
      setSheets(await startChildSheets(childId));
    } catch (e) {
      setErrorKey(sheetErrorKey(e));
      void loadSheets().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!isAppearanceComplete(draft)) return;
    setSaving(true);
    setErrorKey(null);
    try {
      const body: AppearanceInput = {
        ...draft,
        companion_detail: draft.companion_detail.trim(),
        companion_kind_other: draft.companion_kind === "other" ? (draft.companion_kind_other ?? "").trim() : "",
        child_detail: (draft.child_detail ?? "").trim(),
      };
      await putChildAppearance(childId, body);
      await refresh();
      const out = await loadSheets();
      setView("sheets");
      if (shouldAutoStartBatch(out)) await startBatch();
    } catch (e) {
      setErrorKey(sheetErrorKey(e));
    } finally {
      setSaving(false);
    }
  }

  async function choose(sheetId: string) {
    setBusy(true);
    setErrorKey(null);
    try {
      await chooseChildSheet(childId, sheetId);
      await Promise.all([refresh(), loadSheets()]);
    } catch (e) {
      setErrorKey(sheetErrorKey(e));
    } finally {
      setBusy(false);
    }
  }

  if (!child || view === null) {
    return (
      <PageLayout>
        <Container className="pt-16 flex justify-center">
          <MutedText>{t("common.loading")}</MutedText>
        </Container>
      </PageLayout>
    );
  }

  const header = (
    <>
      <BackButton onClick={() => navigate("/settings")} />
      <div>
        <Headline>{t("appearance.title", { name })}</Headline>
        <MutedText className="mt-2">{t("appearance.intro", { name })}</MutedText>
      </div>
    </>
  );

  if (view === "form") {
    return (
      <PageLayout>
        <Container className="pt-6 pb-8 flex flex-col gap-6">
          {header}
          {justCreated && <MutedText>{t("appearance.optional", { name })}</MutedText>}

          <section className="flex flex-col gap-5">
            <h2 className="font-heading font-bold text-lg">{t("appearance.childSection", { name })}</h2>
            <ChoiceGroup label={t("appearance.presentedAsLabel")} options={PRESENTED_AS_OPTIONS} value={draft.presented_as} onChange={(v) => set("presented_as", v)} />
            <ChoiceGroup label={t("appearance.skinLabel")} options={SKIN_OPTIONS} value={draft.skin} onChange={(v) => set("skin", v)} />
            <ChoiceGroup label={t("appearance.hairColorLabel")} options={HAIR_COLOR_OPTIONS} value={draft.hair_color} onChange={(v) => set("hair_color", v)} />
            <ChoiceGroup label={t("appearance.hairLengthLabel")} options={HAIR_LENGTH_OPTIONS} value={draft.hair_length} onChange={(v) => set("hair_length", v)} />
            <ChoiceGroup label={t("appearance.hairTextureLabel")} options={HAIR_TEXTURE_OPTIONS} value={draft.hair_texture} onChange={(v) => set("hair_texture", v)} />
            <ChoiceGroup label={t("appearance.hairstyleLabel")} options={HAIRSTYLE_OPTIONS} value={draft.hairstyle} onChange={(v) => set("hairstyle", v)} />
            <ChoiceGroup
              label={t("appearance.glassesLabel")}
              options={YES_NO}
              value={draft.glasses ? "yes" : "no"}
              onChange={(v) => set("glasses", v === "yes")}
            />
            <ChoiceGroup label={t("appearance.pyjamaColorLabel")} options={PYJAMA_COLOR_OPTIONS} value={draft.pyjama_color} onChange={(v) => set("pyjama_color", v)} />
            <ChoiceGroup label={t("appearance.pyjamaPatternLabel")} options={PYJAMA_PATTERN_OPTIONS} value={draft.pyjama_pattern} onChange={(v) => set("pyjama_pattern", v)} />
            <div>
              <Label>{t("appearance.childDetailLabel", { name })}</Label>
              <Textarea
                rows={2}
                value={draft.child_detail ?? ""}
                maxLength={CHILD_DETAIL_MAX_LENGTH}
                onChange={(e) => set("child_detail", clampChildDetail(e.target.value))}
                placeholder={t("appearance.childDetailPlaceholder")}
              />
              <CharCounter length={(draft.child_detail ?? "").length} max={CHILD_DETAIL_MAX_LENGTH} />
              <MutedText className="mt-1">{t("appearance.childDetailHint")}</MutedText>
            </div>
          </section>

          <section className="flex flex-col gap-5">
            <div>
              <h2 className="font-heading font-bold text-lg">{t("appearance.companionSection")}</h2>
              {child.companion && (
                <MutedText className="mt-1">{t("appearance.companionName", { companion: child.companion })}</MutedText>
              )}
            </div>
            <ChoiceGroup label={t("appearance.companionKindLabel")} options={COMPANION_KIND_OPTIONS} value={draft.companion_kind} onChange={(v) => set("companion_kind", v)} />
            {draft.companion_kind === "other" && (
              <div>
                <Label>{t("appearance.companionKindOtherLabel")}</Label>
                <Input
                  value={draft.companion_kind_other ?? ""}
                  maxLength={COMPANION_KIND_OTHER_MAX_LENGTH}
                  onChange={(e) => set("companion_kind_other", clampCompanionKindOther(e.target.value))}
                  placeholder={t("appearance.companionKindOtherPlaceholder")}
                />
                <MutedText className="mt-1">{t("appearance.companionKindOtherHint")}</MutedText>
              </div>
            )}
            <ChoiceGroup label={t("appearance.companionColorLabel")} options={COMPANION_COLOR_OPTIONS} value={draft.companion_color} onChange={(v) => set("companion_color", v)} />
            <div>
              <Label>{t("appearance.companionDetailLabel")}</Label>
              <Textarea
                rows={3}
                value={draft.companion_detail ?? ""}
                maxLength={COMPANION_DETAIL_MAX_LENGTH}
                onChange={(e) => set("companion_detail", clampCompanionDetail(e.target.value))}
                placeholder={t("appearance.companionDetailPlaceholder")}
              />
              <CharCounter length={(draft.companion_detail ?? "").length} max={COMPANION_DETAIL_MAX_LENGTH} />
              <MutedText className="mt-1">{t("appearance.companionDetailHint")}</MutedText>
            </div>
          </section>

          {errorKey && <ErrorBanner>{t(errorKey)}</ErrorBanner>}

          <div className="flex flex-col gap-2">
            <Button onClick={() => void save()} disabled={!complete || saving} className="w-full text-base py-4">
              {saving ? t("appearance.saving") : t("appearance.save")}
            </Button>
            {!complete && <MutedText className="text-center">{t("appearance.incomplete")}</MutedText>}
            {child.appearance ? (
              <Button variant="ghost" onClick={() => setView("sheets")}>{t("common.cancel")}</Button>
            ) : justCreated ? (
              <Button variant="ghost" onClick={() => navigate("/settings")}>{t("appearance.later")}</Button>
            ) : null}
          </div>
        </Container>
      </PageLayout>
    );
  }

  const exhausted = !!sheets && !sheets.generating && sheets.batches_left === 0;
  const emptyBatch = !!sheets && !sheets.generating && candidates.length === 0 && !chosen;
  const canMore = canRequestNewBatch(sheets);

  return (
    <PageLayout>
      <Container className="pt-6 pb-8 flex flex-col gap-5">
        {header}

        {errorKey && <ErrorBanner>{t(errorKey)}</ErrorBanner>}

        {stale && !generating && (
          <Card className="flex flex-col gap-3">
            <MutedText>{t("appearance.staleHint", { name })}</MutedText>
            {candidates.length > 0 ? (
              <MutedText>{t("appearance.staleChooseNew")}</MutedText>
            ) : canMore ? (
              <Button onClick={() => void startBatch()} disabled={busy}>{t("appearance.newBatchForNewLook")}</Button>
            ) : (
              <MutedText>{t("appearance.batchesExhaustedHint")}</MutedText>
            )}
          </Card>
        )}

        {chosen && (
          <section className="flex flex-col gap-2">
            <h2 className="font-heading font-bold text-lg">{t("appearance.chosenTitle", { name })}</h2>
            <SheetCard childId={childId} sheet={chosen} name={name} chosen busy={busy} />
          </section>
        )}

        {(showDrawingCard || displayed) && (
          <section className="flex flex-col gap-3">
            <h2 className="font-heading font-bold text-lg">
              {chosen && !stale ? t("appearance.otherCandidates") : t("appearance.candidatesTitle", { name })}
            </h2>
            {(!chosen || stale) && <MutedText>{t("appearance.candidatesHint")}</MutedText>}

            {showDrawingCard ? (
              <DrawingCard name={name} />
            ) : (
              displayed && (
                <SheetCard
                  childId={childId}
                  sheet={displayed}
                  name={name}
                  chosen={false}
                  busy={busy}
                  onChoose={() => void choose(displayed.id)}
                  chooseLabel={t("appearance.chooseCharacter")}
                />
              )
            )}

            {previousRow.length > 0 && (
              <div className={`flex flex-col gap-2 ${showDrawingCard ? "opacity-60" : ""}`}>
                <p className="text-xs font-heading font-semibold uppercase tracking-wide text-violet-soft">
                  {t("appearance.previousProposals")}
                </p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {previousRow.map((sheet) => (
                    <SheetThumbnailButton
                      key={sheet.id}
                      childId={childId}
                      sheet={sheet}
                      name={name}
                      disabled={showDrawingCard}
                      onSelect={() => setDisplayedId(sheet.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {emptyBatch && !stale && (
          <MutedText>{sheets.batches_left > 0 ? t("appearance.emptyBatch") : t("appearance.emptyNoBatch")}</MutedText>
        )}

        {canMore && (stale ? candidates.length > 0 : candidates.length > 0 || emptyBatch || chosen) && (
          <Button variant="secondary" onClick={() => void startBatch()} disabled={busy}>
            {t("appearance.regenerate")}
          </Button>
        )}

        {exhausted && (candidates.length > 0 || (chosen && !stale)) && <MutedText>{t("appearance.batchesExhaustedHint")}</MutedText>}

        <Button variant="ghost" onClick={() => setView("form")}>{t("appearance.editDescription")}</Button>
        <Button variant={chosen && !stale ? "primary" : "ghost"} onClick={() => navigate("/settings")}>
          {chosen || generating ? t("appearance.done") : t("appearance.later")}
        </Button>
      </Container>
    </PageLayout>
  );
}
